import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import BottomNav from '@/components/BottomNav';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Flame,
  Zap,
  Rocket,
  Trophy,
  Shield,
  Target,
  Award,
  Crown,
  ArrowLeft,
  CheckCircle,
  Loader2,
  Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BADGE_ICONS = { flame: Flame, zap: Zap, rocket: Rocket, trophy: Trophy, shield: Shield, target: Target, award: Award, crown: Crown };
const DIFFICULTY_COLORS = { easy: 'bg-emerald-100 text-emerald-700', medium: 'bg-amber-100 text-amber-700', hard: 'bg-red-100 text-red-700' };

const fmt = (amount) => {
  const n = Number(amount) || 0;
  return '\u20a6' + n.toLocaleString();
};

const ActiveCard = ({ ch, checkinId, onCheckin }) => {
  const iconKey = ch.badge_icon || 'flame';
  const Icon = BADGE_ICONS[iconKey] || Flame;
  const completedDays = ch.completed_days || 0;
  const durationDays = ch.duration_days || 1;
  const progressPct = Math.round((completedDays / durationDays) * 100);
  const streak = ch.current_streak || 0;
  const totalSaved = ch.total_saved || 0;
  const targetAmount = ch.target_amount || 0;
  const dailyAmount = ch.daily_amount || 0;
  const badges = ch.badges_earned || [];
  const badgeCount = badges.length;
  const dates = ch.checkin_dates || [];
  const today = new Date().toISOString().split('T')[0];
  const checkedInToday = dates.includes(today);
  const isLoading = checkinId === ch.id;
  const name = ch.name || '';

  return (
    <Card className="glass-card" data-testid={'active-' + ch.id}>
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-xl bg-[#0B1F3A]">
            <Icon size={22} className="text-white" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-[#0B1F3A] text-lg">{name}</p>
            <p className="text-sm text-slate-500">Day {completedDays} of {durationDays}</p>
          </div>
          {streak > 0 && (
            <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-100">
              <Flame size={14} className="text-orange-500" />
              <span className="text-sm font-black text-orange-600">{streak}</span>
            </div>
          )}
        </div>
        <div className="mb-4">
          <div className="flex justify-between text-xs mb-1.5">
            <span className="text-slate-500">Progress</span>
            <span className="font-bold text-[#2272C5]">{progressPct}%</span>
          </div>
          <Progress value={progressPct} className="h-2.5" />
        </div>
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="p-2 rounded-xl bg-slate-50 text-center">
            <p className="text-[10px] text-slate-500">Saved</p>
            <p className="text-sm font-bold text-[#0B1F3A]">{fmt(totalSaved)}</p>
          </div>
          <div className="p-2 rounded-xl bg-slate-50 text-center">
            <p className="text-[10px] text-slate-500">Streak</p>
            <p className="text-sm font-bold text-orange-600">{streak} days</p>
          </div>
          <div className="p-2 rounded-xl bg-slate-50 text-center">
            <p className="text-[10px] text-slate-500">Target</p>
            <p className="text-sm font-bold text-[#0B1F3A]">{fmt(targetAmount)}</p>
          </div>
        </div>
        {badgeCount > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {badges.map(b => (
              <span key={b} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
                <Star size={10} /> {b}
              </span>
            ))}
          </div>
        )}
        <Button
          onClick={() => onCheckin(ch.id)}
          disabled={checkedInToday || isLoading}
          className={'w-full h-14 rounded-xl font-bold text-base ' + (checkedInToday ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-emerald-600 hover:bg-emerald-700 text-white')}
          data-testid={'checkin-' + ch.id}
        >
          {isLoading && <Loader2 className="animate-spin mr-2" size={18} />}
          {!isLoading && checkedInToday && <><CheckCircle size={18} className="mr-2" /> Checked In Today</>}
          {!isLoading && !checkedInToday && <><Flame size={18} className="mr-2" /> Save {fmt(dailyAmount)} Today</>}
        </Button>
      </CardContent>
    </Card>
  );
};

const CompletedCard = ({ ch }) => {
  const iconKey = ch.badge_icon || 'trophy';
  const Icon = BADGE_ICONS[iconKey] || Trophy;
  const name = ch.name || '';
  const totalSaved = ch.total_saved || 0;
  const longestStreak = ch.longest_streak || 0;
  const badges = ch.badges_earned || [];
  const badgeCount = badges.length;

  return (
    <Card className="glass-card border-2 border-emerald-200" data-testid={'completed-' + ch.id}>
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-3 rounded-xl bg-emerald-500">
            <Icon size={22} className="text-white" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-[#0B1F3A]">{name}</p>
            <p className="text-sm text-emerald-600 font-semibold">Completed!</p>
          </div>
          <CheckCircle size={24} className="text-emerald-500" />
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="p-2 rounded-xl bg-emerald-50 text-center">
            <p className="text-[10px] text-emerald-600">Saved</p>
            <p className="text-sm font-bold text-emerald-700">{fmt(totalSaved)}</p>
          </div>
          <div className="p-2 rounded-xl bg-emerald-50 text-center">
            <p className="text-[10px] text-emerald-600">Best Streak</p>
            <p className="text-sm font-bold text-emerald-700">{longestStreak} days</p>
          </div>
          <div className="p-2 rounded-xl bg-emerald-50 text-center">
            <p className="text-[10px] text-emerald-600">Badges</p>
            <p className="text-sm font-bold text-emerald-700">{badgeCount}</p>
          </div>
        </div>
        {badgeCount > 0 && (
          <div className="flex flex-wrap gap-2">
            {badges.map(b => (
              <span key={b} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
                <Star size={10} /> {b}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const TemplateCard = ({ t, isActive, onJoin, joining }) => {
  const iconKey = t.badge_icon || 'flame';
  const Icon = BADGE_ICONS[iconKey] || Flame;
  const difficulty = t.difficulty || 'easy';
  const diffClass = DIFFICULTY_COLORS[difficulty] || DIFFICULTY_COLORS.easy;
  const name = t.name || '';
  const desc = t.description || '';
  const daily = t.daily_amount || 0;
  const dur = t.duration_days || 0;
  const target = t.target_amount || 0;

  return (
    <Card className="glass-card" data-testid={'template-' + t.id}>
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-3 rounded-xl bg-[#0B1F3A]">
            <Icon size={22} className="text-white" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-[#0B1F3A] text-lg">{name}</p>
            <p className="text-sm text-slate-500">{desc}</p>
          </div>
          <span className={'text-xs font-bold px-2 py-1 rounded-full ' + diffClass}>{difficulty}</span>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="p-2 rounded-xl bg-slate-50 text-center">
            <p className="text-[10px] text-slate-500">Daily</p>
            <p className="text-sm font-bold text-[#0B1F3A]">{fmt(daily)}</p>
          </div>
          <div className="p-2 rounded-xl bg-slate-50 text-center">
            <p className="text-[10px] text-slate-500">Duration</p>
            <p className="text-sm font-bold text-[#0B1F3A]">{dur} days</p>
          </div>
          <div className="p-2 rounded-xl bg-slate-50 text-center">
            <p className="text-[10px] text-slate-500">Target</p>
            <p className="text-sm font-bold text-[#0B1F3A]">{fmt(target)}</p>
          </div>
        </div>
        <Button
          onClick={() => onJoin(t)}
          disabled={isActive || joining}
          className={'w-full h-12 rounded-xl font-bold ' + (isActive ? 'bg-slate-300 text-slate-600' : 'bg-[#0B1F3A] hover:bg-[#1a3a8f] text-white')}
          data-testid={'join-' + t.id}
        >
          {isActive ? 'Already Active' : 'Start Challenge'}
        </Button>
      </CardContent>
    </Card>
  );
};

const Challenges = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [myChallenges, setMyChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [checkinId, setCheckinId] = useState(null);
  const [showJoinDialog, setShowJoinDialog] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [tplRes, myRes] = await Promise.all([
        axios.get(API_URL + '/api/challenges/templates', { headers }),
        axios.get(API_URL + '/api/challenges/my', { headers }),
      ]);
      setTemplates(tplRes.data);
      setMyChallenges(myRes.data);
    } catch { toast.error('Failed to load challenges'); }
    finally { setLoading(false); }
  };

  const handleJoin = async (templateId) => {
    setJoining(true);
    try {
      await axios.post(API_URL + '/api/challenges/join', { template_id: templateId }, { headers: getAuthHeaders() });
      toast.success('Challenge started!');
      setShowJoinDialog(null);
      fetchData();
    } catch (err) {
      const msg = err.response && err.response.data ? err.response.data.detail : 'Failed to join';
      toast.error(msg);
    } finally { setJoining(false); }
  };

  const handleCheckin = async (challengeId) => {
    setCheckinId(challengeId);
    try {
      const res = await axios.post(API_URL + '/api/challenges/' + challengeId + '/checkin', {}, { headers: getAuthHeaders() });
      const d = res.data;
      toast.success(d.message);
      const nb = d.new_badges || [];
      nb.forEach(b => toast.success('Badge earned: ' + b + '!'));
      fetchData();
    } catch (err) {
      const msg = err.response && err.response.data ? err.response.data.detail : 'Check-in failed';
      toast.error(msg);
    } finally { setCheckinId(null); }
  };

  const activeIds = new Set(myChallenges.filter(c => c.status === 'active').map(c => c.template_id));
  const activeChallenges = myChallenges.filter(c => c.status === 'active');
  const completedChallenges = myChallenges.filter(c => c.status === 'completed');
  const activeCount = activeChallenges.length;
  const completedCount = completedChallenges.length;
  const defaultTab = activeCount > 0 ? 'active' : 'browse';

  // Extract dialog data into variables to avoid deep chaining in JSX
  const dialogName = showJoinDialog ? (showJoinDialog.name || '') : '';
  const dialogDaily = showJoinDialog ? (showJoinDialog.daily_amount || 0) : 0;
  const dialogDays = showJoinDialog ? (showJoinDialog.duration_days || 0) : 0;
  const dialogId = showJoinDialog ? (showJoinDialog.id || '') : '';
  const dialogOpen = showJoinDialog !== null;

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 pb-24">
        <div className="app-container px-4 pt-6">
          <Skeleton className="h-8 w-48 mb-6" />
          <Skeleton className="h-40 w-full rounded-2xl mb-4" />
          <Skeleton className="h-40 w-full rounded-2xl" />
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="challenges-screen">
      <div className="app-container px-4 pt-6">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/')} className="p-2 rounded-xl hover:bg-slate-100">
            <ArrowLeft size={20} className="text-[#0B1F3A]" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[#0B1F3A]">Savings Challenges</h1>
            <p className="text-sm text-slate-500">Build your savings habit with daily streaks</p>
          </div>
        </div>

        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="active">Active ({activeCount})</TabsTrigger>
            <TabsTrigger value="browse">Browse</TabsTrigger>
            <TabsTrigger value="completed">Done ({completedCount})</TabsTrigger>
          </TabsList>

          <TabsContent value="active" className="mt-0 space-y-4">
            {activeCount === 0 ? (
              <div className="text-center py-12">
                <Flame size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 font-semibold">No active challenges</p>
                <p className="text-sm text-slate-400 mt-1">Browse challenges to start your savings streak!</p>
              </div>
            ) : (
              activeChallenges.map(ch => <ActiveCard key={ch.id} ch={ch} checkinId={checkinId} onCheckin={handleCheckin} />)
            )}
          </TabsContent>

          <TabsContent value="browse" className="mt-0 space-y-4">
            {templates.map(t => (
              <TemplateCard key={t.id} t={t} isActive={activeIds.has(t.id)} onJoin={setShowJoinDialog} joining={joining} />
            ))}
          </TabsContent>

          <TabsContent value="completed" className="mt-0 space-y-4">
            {completedCount === 0 ? (
              <div className="text-center py-12">
                <Trophy size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 font-semibold">No completed challenges yet</p>
                <p className="text-sm text-slate-400 mt-1">Complete a challenge to earn badges!</p>
              </div>
            ) : (
              completedChallenges.map(ch => <CompletedCard key={ch.id} ch={ch} />)
            )}
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={dialogOpen} onOpenChange={() => setShowJoinDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0B1F3A] font-black text-xl">Start Challenge?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 mt-2">
                <span className="text-slate-700 font-semibold block">
                  {"You're about to join: "}
                  <span className="text-[#0B1F3A] font-black">{dialogName}</span>
                </span>
                <div className="p-4 rounded-xl bg-[#0B1F3A] text-white text-center">
                  <span className="text-2xl font-black block">{fmt(dialogDaily)}</span>
                  <span className="text-blue-200 text-sm block mt-1">per day for {dialogDays} days</span>
                </div>
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
                  <span className="text-amber-800 text-sm font-semibold block">
                    Each daily check-in will save {fmt(dialogDaily)} from your available balance. Funds are locked until the challenge is complete, then released back to you.
                  </span>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-semibold border-2">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleJoin(dialogId)}
              disabled={joining}
              className="bg-[#0B1F3A] hover:bg-[#1a3a8f] text-white font-bold"
            >
              {joining ? 'Starting...' : "Let's Go!"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BottomNav />
    </div>
  );
};

export default Challenges;
